import React from 'react';
import _get from 'lodash/get';
import _size from 'lodash/size';
import _isArray from 'lodash/isArray';
import _isEmpty from 'lodash/isEmpty';
import { ReactComponent as DownSmall } from 'assets/img/icons/down-small.svg';

import { If } from 'utils';
import { formatPrice } from 'utils/formatters';
import { UiKit } from 'components';
import createPriceRange from './createPriceRange';
import { PRODUCTS_MODAL_TABLE_HEADERS as HEADERS } from '../stubs';
import { TableHighlight } from 'components/Tables';

export default function createModalTableRowsData({
  products = [],
  searchQuery = '',
  selected = {},
  onSelectProduct = () => {},
  onSelectProductVariant = () => {}
}) {
  if (!_isArray(products)) return [];

  return products.map(product => {
    const productId = _get(product, 'ObjectId', '');
    const productVariants = _get(product, 'productVariants', []);
    const currency = _get(product, 'currency', '');
    const selectedVariants = _get(selected, `${productId}.variants`, {});

    return {
      rowData: createProductRowData({
        product,
        searchQuery,
        selected: selectedVariants,
        onToggle: onSelectProduct.bind(null, product)
      }),
      collapsedRowsData: createProductVariantRowData({
        productVariants,
        currency,
        selected: selectedVariants,
        onToggle: onSelectProductVariant.bind(null, product)
      })
    };
  });
}

function createProductRowData({
  product = {},
  searchQuery = '',
  selected = {},
  onToggle = () => {}
} = {}) {
  const id = _get(product, 'ObjectId');
  const imageUrl = _get(product, 'image_url', '');
  const productVariants = _get(product, 'productVariants', []);
  const priceRange = _get(product, 'price_range', []);
  const currency = _get(product, 'currency', '');

  const formattedPriceRange = createPriceRange(priceRange, currency);
  const hasVariants = !_isEmpty(productVariants);

  const { isChecked, isBlocked } = productVariants.reduce(
    (acc, productVariant) => {
      const id = _get(productVariant, 'objectID');
      const exception = _get(productVariant, 'exception', null);
      acc.isChecked = acc.isChecked && id in selected;
      acc.isBlocked = acc.isBlocked && !!exception;
      return acc;
    },
    {
      isChecked: productVariants.length > 0,
      isBlocked: productVariants.length > 0
    }
  );

  const onToggleVariants = () => {
    if (!hasVariants || isBlocked) return;
    onToggle(productVariants);
  };

  return {
    id,
    disabled: !hasVariants,
    values: [
      {
        lg: HEADERS[0].lg,
        CustomComponent: (
          <>
            <div className="product-title-cell">
              <UiKit.Checkbox
                checked={isChecked}
                onChange={onToggleVariants}
                disabled={!hasVariants || isBlocked}
                label={
                  <UiKit.Grid
                    templateColumns="auto 1fr"
                    gap="8"
                    className="align-items-center"
                  >
                    <div
                      className="image"
                      style={{ backgroundImage: `url(${imageUrl})` }}
                    />
                    <TableHighlight
                      data={product}
                      attribute="title"
                      text={searchQuery}
                      className="name truncate"
                    />
                  </UiKit.Grid>
                }
                tooltipText={
                  hasVariants && isBlocked ? `All variants blocked` : ''
                }
              />
              <UiKit.Grid
                gap="4"
                templateColumns="auto 1fr"
                className="product-variants-count"
              >
                <span>{_size(productVariants)} variants</span>
                <If test={hasVariants}>
                  <DownSmall className="arrow-icon" />
                </If>
              </UiKit.Grid>
            </div>
          </>
        )
      },
      {
        lg: HEADERS[1].lg,
        title: ''
      },
      {
        lg: HEADERS[2].lg,
        title: formattedPriceRange,
        customClass: 'small-text text-left'
      }
    ]
  };
}

function createProductVariantRowData({
  productVariants = [],
  currency = '',
  selected = {},
  onToggle = () => {}
} = {}) {
  return productVariants.map(productVariant => {
    const id = _get(productVariant, 'objectID');
    const productVariantName = _get(productVariant, 'title', '');
    const imageUrl = _get(productVariant, 'image_url', '');
    const price = _get(productVariant, 'price', '');
    const sku = _get(productVariant, 'sku', '');
    const exception = _get(productVariant, 'exception.name', null);
    const isBlocked = !!exception;
    const isChecked = id in selected;

    const onToggleVariant = () => {
      if (isBlocked) return;
      onToggle(productVariant);
    };

    return {
      id,
      disabled: isBlocked,
      values: [
        {
          lg: HEADERS[0].lg,
          CustomComponent: (
            <UiKit.Checkbox
              checked={isChecked}
              onChange={onToggleVariant}
              disabled={isBlocked}
              className="product-variant-title-cell"
              label={
                <UiKit.Grid
                  templateColumns="auto 1fr"
                  gap="8"
                  className="align-items-center"
                >
                  <div
                    className="image"
                    style={{ backgroundImage: `url(${imageUrl})` }}
                  />
                  <p className="name m-0">{productVariantName}</p>
                </UiKit.Grid>
              }
              tooltipText={
                isBlocked ? `Included in "${exception}" exception` : ''
              }
            />
          )
        },
        {
          lg: HEADERS[1].lg,
          title: sku,
          customClass: 'light-text'
        },
        {
          lg: HEADERS[2].lg,
          title: formatPrice(price, currency),
          customClass: 'light-text'
        }
      ]
    };
  });
}
